import React, { useEffect, useState } from "react";

import Swal from "sweetalert2";

import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaTimes,
    FaSave,
    FaImage
} from "react-icons/fa";

import API_BASE_URL from "../config/api";


const Gallery = () => {

    // =========================================================
    // API
    // =========================================================

    const API_URL = API_BASE_URL.replace(/\/$/, "");

    const STORAGE_BASE_URL = API_URL.replace(/\/api$/, "");

    const IMAGE_URL = `${STORAGE_BASE_URL}/storage/gallery`;


    // =========================================================
    // STATES
    // =========================================================

    const [gallery, setGallery] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editingId, setEditingId] = useState(null);

    const [imagePreview, setImagePreview] = useState("");

    const [form, setForm] = useState({
        image: null
    });


    // =========================================================
    // FETCH GALLERY
    // =========================================================

    const fetchGallery = async () => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_URL}/admin/gallery`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result = await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Unable to fetch gallery"
                );
            }


            setGallery(result.data || []);

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // LOAD
    // =========================================================

    useEffect(() => {

        fetchGallery();

    }, []);


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setForm({
            image: null
        });

        setImagePreview("");

        setEditingId(null);
    };


    // =========================================================
    // ADD
    // =========================================================

    const handleAdd = () => {

        resetForm();

        setShowForm(true);
    };


    // =========================================================
    // CANCEL
    // =========================================================

    const handleCancel = () => {

        resetForm();

        setShowForm(false);
    };


    // =========================================================
    // IMAGE CHANGE
    // =========================================================

    const handleImageChange = (e) => {

        const file = e.target.files[0];

        if (!file) {
            return;
        }


        setForm({
            image: file
        });


        const previewUrl = URL.createObjectURL(file);

        setImagePreview(previewUrl);
    };


    // =========================================================
    // EDIT
    // =========================================================

    const handleEdit = async (id) => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_URL}/admin/gallery/${id}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result = await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Unable to get gallery"
                );
            }


            const data = result.data;


            setEditingId(data.id);


            setForm({
                image: null
            });


            if (data.image) {

                setImagePreview(
                    `${IMAGE_URL}/${data.image}`
                );
            }


            setShowForm(true);

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        try {

            if (!form.image) {

                Swal.fire({
                    icon: "warning",
                    title: "Image Required",
                    text: "Please select an image."
                });

                return;
            }


            setSaving(true);


            const formData = new FormData();


            formData.append(
                "image",
                form.image
            );


            let url = `${API_URL}/admin/gallery`;


            if (editingId) {

                url =
                    `${API_URL}/admin/gallery/${editingId}`;
            }


            const response = await fetch(
                url,
                {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result = await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Something went wrong"
                );
            }


            Swal.fire({
                icon: "success",
                title: "Success",
                text: result.message,
                timer: 1500,
                showConfirmButton: false
            });


            resetForm();

            setShowForm(false);

            fetchGallery();

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // DELETE
    // =========================================================

    const handleDelete = async (id) => {

        const confirm = await Swal.fire({

            icon: "warning",

            title: "Delete Popup Image?",

            text: "This image will be permanently deleted.",

            showCancelButton: true,

            confirmButtonText: "Yes, Delete",

            cancelButtonText: "Cancel"
        });


        if (!confirm.isConfirmed) {
            return;
        }


        try {

            const response = await fetch(
                `${API_URL}/admin/gallery/${id}`,
                {
                    method: "DELETE",
                    credentials: "include",
                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result = await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Unable to delete image"
                );
            }


            Swal.fire({
                icon: "success",
                title: "Deleted",
                text: result.message,
                timer: 1500,
                showConfirmButton: false
            });


            fetchGallery();

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message
            });
        }
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

       <div
            className="container-fluid"
            style={{
                maxHeight: "85vh",
                overflowY: "auto"
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h3 className="mb-1">
                        Gallery Popup
                    </h3>

                    <p className="text-muted mb-0">
                        Manage Popup Images
                    </p>

                </div>


                {!showForm && (

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                    >

                        <FaPlus className="me-2" />

                        Add Popup Image

                    </button>

                )}

            </div>


            {/* =================================================
                FORM
            ================================================= */}

            {showForm && (

                <div className="card shadow-sm mb-4">

                    <div className="card-header d-flex justify-content-between align-items-center">

                        <h5 className="mb-0">

                            {editingId
                                ? "Edit Popup Image"
                                : "Add Popup Image"
                            }

                        </h5>


                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleCancel}
                        >

                            <FaTimes />

                        </button>

                    </div>


                    <div className="card-body">

                        <form onSubmit={handleSubmit}>

                            <div className="row">

                                <div className="col-md-6">

                                    <label className="form-label fw-semibold">

                                        Popup Image

                                        <span className="text-danger">
                                            {" "}*
                                        </span>

                                    </label>


                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        onChange={handleImageChange}
                                    />


                                    <small className="text-muted">

                                        JPG, JPEG, PNG or WEBP.
                                        Maximum 5MB.

                                    </small>


                                    {imagePreview && (

                                        <div className="mt-3">

                                            <img
                                                src={imagePreview}
                                                alt="Popup Preview"
                                                style={{
                                                    width: "300px",
                                                    height: "180px",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "1px solid #ddd"
                                                }}
                                            />

                                        </div>

                                    )}

                                </div>


                                <div className="col-12 mt-4">

                                    <button
                                        type="submit"
                                        className="btn btn-success me-2"
                                        disabled={saving}
                                    >

                                        <FaSave className="me-2" />

                                        {saving
                                            ? "Saving..."
                                            : editingId
                                                ? "Update Image"
                                                : "Save Image"
                                        }

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCancel}
                                        disabled={saving}
                                    >

                                        <FaTimes className="me-2" />

                                        Cancel

                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* =================================================
                GALLERY LIST
            ================================================= */}

            <div className="card shadow-sm">

                <div className="card-header">

                    <h5 className="mb-0">
                        Popup Images
                    </h5>

                </div>


                <div className="card-body">

                    {loading ? (

                        <div className="text-center py-5">

                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />

                            <p className="mt-2">
                                Loading...
                            </p>

                        </div>

                    ) : gallery.length === 0 ? (

                        <div className="text-center py-5">

                            <FaImage
                                size={45}
                                className="text-muted mb-3"
                            />

                            <h5>
                                No Popup Images Found
                            </h5>

                            <p className="text-muted">
                                Add your first Popup image.
                            </p>

                        </div>

                    ) : (

                        <div className="row g-4">

                            {gallery.map((item) => (

                                <div
                                    className="col-xl-3 col-lg-4 col-md-6"
                                    key={item.id}
                                >

                                    <div className="card h-100 shadow-sm">

                                        {/* IMAGE */}

                                        <img
                                            src={`${IMAGE_URL}/${item.image}`}
                                            alt="Popup"
                                            className="card-img-top"
                                            style={{
                                                height: "200px",
                                                objectFit: "cover"
                                            }}
                                        />


                                        <div className="card-body">

                                            <div className="d-flex justify-content-between align-items-center">

                                                <span className="badge bg-primary">
                                                    Popup
                                                </span>

                                                <small className="text-muted">
                                                    #{item.id}
                                                </small>

                                            </div>


                                            <p className="text-muted small mt-2 mb-0">

                                                {item.upload_date}

                                            </p>

                                        </div>


                                        {/* ACTIONS */}

                                        <div className="card-footer bg-white">

                                            <div className="d-flex gap-2">

                                                <button
                                                    type="button"
                                                    className="btn btn-warning btn-sm flex-fill"
                                                    onClick={() =>
                                                        handleEdit(item.id)
                                                    }
                                                >

                                                    <FaEdit className="me-1" />

                                                    Edit

                                                </button>


                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm flex-fill"
                                                    onClick={() =>
                                                        handleDelete(item.id)
                                                    }
                                                >

                                                    <FaTrash className="me-1" />

                                                    Delete

                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            ))}

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
};


export default Gallery;